import React, { useEffect, useMemo, useState } from 'react'
import { useI18n } from '../i18n/index.jsx'
import ReactGA from 'react-ga4'
import { logReferralEvent } from '../components/ReferralManager'

// Helper to show base text if translation missing for plan titles/descriptions
function getBasePlanText(key) {
    const baseTexts = {
        'pricing.plan.free.title': 'Gratuit',
        'pricing.plan.free.description': 'Fonctionnalités de base',
        'pricing.plan.pro.title': 'Pro',
        'pricing.plan.pro.description': 'Pour aller plus loin',
        'pricing.plan.max.title': 'Max',
        'pricing.plan.max.description': 'Puissance maximale'
    };
    return baseTexts[key] || key;
}

// Helper to show base text if translation missing
function getBaseFeatureText(key) {
    const baseTexts = {
        'pricing.features.requests.500': '500 requests',
        'pricing.features.memory.10000': '10 000 memories',
        'pricing.features.support.48h': 'Support : 48h',
        'pricing.features.community.discord': 'Communauty : Discord',
        'pricing.features.tester.none': 'Test : Aucun',
        'pricing.features.performance.none': 'Performance : Aucun',
        'pricing.features.group.none': 'Groupe : Aucun',
        'pricing.features.requests.2500': '2 500 requêtes',
        'pricing.features.memory.50000': '50 000 mémoire',
        'pricing.features.support.24h': 'Support : 24h',
        'pricing.features.tester.beta': 'Testeur : Beta test',
        'pricing.features.performance.best': 'Performance : Meilleur résultat et stockage',
        'pricing.features.requests.30000': '30 000 requêtes',
        'pricing.features.memory.500000': '500 000 mémoire',
        'pricing.features.support.6h': 'Support : 6h',
        'pricing.features.group.private': 'Groupe : Groupe privé'
    };
    return baseTexts[key] || key;
}

// Stripe product IDs
const PRODUCT_IDS = {
    pro: {
        monthly: 'prod_TGSCozHxcMGKxz', //le seul a changer
        yearly: 'prod_TGSDoLPcQA92Ud',
    },
    max: {
        monthly: 'prod_TGSDKbS5kzVCTP',
        yearly: 'prod_TGSE6l9ut73t6N',
    },
}

// Stripe Checkout links (test)
const CHECKOUT_LINKS = {
    pro: {
        monthly: 'https://buy.stripe.com/7sY3cvesJciO6F0ajF83C00',
        yearly: 'https://buy.stripe.com/8x2eVd0BT5UqgfA8bx83C01',
    },
    max: {
        monthly: 'https://buy.stripe.com/fZucN5esJfv0d3o2Rd83C02',
        yearly: 'https://buy.stripe.com/bJebJ1bgxaaG3sO3Vh83C03',
    }
}

const BASE_PLANS = [
    {
        tier: 'free',
            title: 'Free',
            description: 'Basic features',
        prices: { monthly: 0, yearly: 0 },
        productId: { monthly: null, yearly: null },
        links: { monthly: null, yearly: null },
            features: [
                '500 requests',
                '10,000 memory',
                'Support: 48h',
                'Community: Discord',
                'Limited to Two models'
        ],
        highlight: false,
    },
    {
        tier: 'pro',
            title: 'Pro',
            description: 'Go further',
        prices: { monthly: 9.99, yearly: 99 },
        productId: PRODUCT_IDS.pro,
        links: CHECKOUT_LINKS.pro,
            features: [
                '2,500 requests',
                '50,000 memory',
                'Support: 24h',
                'Community: Discord',
                'Tester: Beta test',
                'Performance: Best result and storage',
                'All the models are available'
            
        ],
        highlight: true,
    },
    {
        tier: 'max',
            title: 'Max',
            description: 'Maximum power',
        prices: { monthly: 19.99, yearly: 199 },
        productId: PRODUCT_IDS.max,
        links: CHECKOUT_LINKS.max,
            features: [
                '30,000 requests',
                '500,000 memory',
                'Support: 6h',
                'Community: Discord',
                'Tester: Beta test',
                'Performance: Best result and storage',
                'Private group',
                'All the models are available'
        ],
        highlight: false,
    },
]


const europeanLangs = ['fr', 'de', 'es', 'it', 'pt', 'nl', 'fi', 'sv', 'da', 'pl', 'cs', 'sk', 'hu', 'el', 'ro', 'bg', 'hr', 'lt', 'lv', 'et', 'sl'];
const getCurrencySymbol = (lang) => {
    if (!lang || typeof lang !== 'string') return '$';
    const prefix = lang.split('-')[0];
    return europeanLangs.includes(prefix) ? '€' : '$';
};

export default function PricingFree() {
    const { t, lang } = useI18n()
    const [billing, setBilling] = useState('monthly') // 'monthly' | 'yearly'
    const [prefilledEmail, setPrefilledEmail] = useState('')
    const [isActive, setIsActive] = useState(false)
    const [loadingStatus, setLoadingStatus] = useState(false)
    const plans = useMemo(() => BASE_PLANS, [])
    const [isLoggedIn, setIsLoggedIn] = useState(false)

    // Track page view
    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: "/pricing-free", title: "Pricing" });
    }, []);

    useEffect(() => {
        // Simple check: token in localStorage
        const token = localStorage.getItem('access_token')
        setIsLoggedIn(!!token)
    }, [])

    useEffect(() => {
        try {
            const params = new URLSearchParams(window.location.search)
            const qEmail = params.get('email')
            const stored = localStorage.getItem('email')
            setPrefilledEmail(qEmail || stored || '')
        } catch {}
    }, [])

    const hrefWithEmail = (base) => {
        if (!prefilledEmail) return base
        const url = new URL(base)
        url.searchParams.set('prefilled_email', prefilledEmail)
        return url.toString()
    }

    // Fetch subscription status when email is known
    useEffect(() => {
        let ignore = false
        async function fetchStatus(email) {
            if (!email) { setIsActive(false); return }
            try {
                setLoadingStatus(true)
                const params = new URLSearchParams({ email })
                const res = await fetch(`https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/subscription/status?${params.toString()}`)
                const json = await res.json()
                if (!ignore) setIsActive(Boolean(json.active))
            } catch (_) {
                if (!ignore) setIsActive(false)
            } finally {
                if (!ignore) setLoadingStatus(false)
            }
        }
        fetchStatus(prefilledEmail)
        return () => { ignore = true }
    }, [prefilledEmail])

    return (
        <section id="pricing" className="min-h-screen flex items-center justify-center py-12 bg-slate-950 relative">
            {/* Bouton retour landing, texte selon langue */}
            <a
                href="/"
                className="absolute left-6 top-6 rounded-lg bg-emerald-500 text-slate-950 px-4 py-2 font-semibold shadow hover:bg-emerald-400 transition"
                style={{zIndex: 10}}
            >
                ← {lang === 'fr' ? "Retour à l'accueil" : "Back to Home"}
            </a>
            <div className="mx-auto max-w-5xl px-4 w-full">
                <div className="mb-12 text-center">
                    <p className="mb-3 font-medium text-emerald-400">Pricing</p>
                    <h2 className="text-3xl font-bold tracking-tight text-white sm:text-5xl">Choose your plan</h2>
                    <p className="mt-3 text-white">Monthly or Annual. With Annual, get 2 months free.</p>
                </div>

                {/* Toggle monthly/yearly */}
                <div className="mb-10 flex items-center justify-center gap-3">
                    <button
                        onClick={() => setBilling('monthly')}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                            billing === 'monthly'
                                ? 'bg-emerald-500 text-slate-950'
                                : 'border border-white/15 bg-white/5 text-white hover:border-emerald-400/60 hover:bg-white/10'
                        }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBilling('yearly')}
                        className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                            billing === 'yearly'
                                ? 'bg-emerald-500 text-slate-950'
                                : 'border border-white/15 bg-white/5 text-white hover:border-emerald-400/60 hover:bg-white/10'
                        }`}
                    >
                        Annual <span className="ml-2 text-emerald-200">(2 months free)</span>
                    </button>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3 items-stretch">
                    {plans.map((p) => {
                        const price = p.prices[billing]
                        const link = p.links[billing]
                        const productId = p.productId[billing]
                        const isPaid = p.tier !== 'free'
                        const disabled = isPaid && isActive
                        const currency = getCurrencySymbol(lang)
                        return (
                            <div
                                key={p.tier}
                                className={`relative flex flex-col justify-between rounded-2xl border border-white/10 bg-gradient-to-b from-white/10 to-white/5 p-6 backdrop-blur-2xl transition hover:-translate-y-0.5 hover:border-emerald-400/40 ${
                                    p.highlight ? 'ring-1 ring-emerald-400/40 shadow-[0_0_1px_rgba(255,255,255,0.25),0_25px_80px_-20px_rgba(16,185,129,0.35)]' : ''
                                }`}
                            >
                                {p.tier === 'pro' && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-slate-950 shadow-md">Popular</div>
                                )}
                                <div className="mb-4 flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-white">{p.title}</h3>
                                    {billing === 'yearly' && isPaid && (
                                        <span className="rounded-md bg-emerald-500/20 px-2 py-1 text-xs font-semibold text-emerald-300">2 months free</span>
                                    )}
                                </div>
                                <p className="mb-6 text-sm text-white">{p.description}</p>
                                <div className="mb-6 flex items-end gap-2">
                                    <p className="text-4xl font-extrabold tracking-tight text-white">{isPaid ? `${currency}${price}` : 'Free'}</p>
                                    {isPaid && (
                                        <span className="mb-1 text-xs font-semibold uppercase text-white">/{billing === 'monthly' ? 'month' : 'year'}</span>
                                    )}
                                </div>
                                <ul className="mb-8 space-y-2.5 text-sm leading-relaxed text-white">
                                    {Array.isArray(p.features) && p.features.map((feature, i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 opacity-80 text-white"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/></svg>
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="text-center mt-auto">
                                    {isPaid ? (
                                        <a
                                            href={disabled ? undefined : isLoggedIn ? hrefWithEmail(link) : '/login?redirect=/pricing'}
                                            onClick={async (e) => {
                                                if (disabled) {
                                                    e.preventDefault();
                                                } else if (isLoggedIn) {
                                                    // ✅ APPELER L'AFFILIATION AVANT DE REDIRIGER VERS STRIPE
                                                    e.preventDefault();
                                                    const userId = localStorage.getItem('user_id');
                                                    const priceId = productId; // price_id Stripe
                                                    
                                                    if (userId) {
                                                        console.log('📤 Appel de logReferralEvent avant redirection Stripe...');
                                                        console.log('Paramètres:', { event: 'payment', userId, price_id: priceId, billing });
                                                        
                                                        await logReferralEvent({
                                                            event: 'payment',
                                                            userId: parseInt(userId),
                                                            price_id: priceId,
                                                            timestamp: Date.now()
                                                        });
                                                        
                                                        console.log('✅ Affiliation trackée! Redirection vers Stripe...');
                                                    }
                                                    
                                                    // Rediriger vers Stripe
                                                    window.open(hrefWithEmail(link), '_blank');
                                                }
                                            }}
                                            aria-disabled={disabled}
                                            target={disabled || !isLoggedIn ? undefined : "_blank"}
                                            rel={disabled || !isLoggedIn ? undefined : "noreferrer"}
                                            className={`inline-flex w-full items-center justify-center rounded-lg px-5 py-3 font-semibold transition ${
                                                disabled
                                                    ? 'cursor-not-allowed bg-slate-600 text-white/50'
                                                    : 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-slate-950 hover:from-emerald-400 hover:to-emerald-300'
                                            }`}
                                        >
                                            {loadingStatus ? 'Checking...' : disabled ? 'Already subscribed' : 'Subscribe'}
                                        </a>
                                    ) : (
                                        <div className="inline-flex w-full items-center justify-center rounded-lg bg-white/10 px-5 py-3 text-white">Included by default</div>
                                    )}
                                    {disabled && <p className="mt-1 text-xs text-emerald-400">Auto-renew enabled</p>}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
